/** The counties the agent works: their labels, and turning one on or off. */
import type { County } from "@/api/models";
import { useSaveCounties, useWorkedCounties } from "@/api/queries";

/** "Harris County", or "2 counties" when the agent works several. */
export function workedLabel(worked: County[]): string {
  if (worked.length === 1) return `${worked[0]?.name ?? ""} County`;
  return `${worked.length} counties`;
}

/** The header button's spoken name: its visible words first, so voice control finds it, then the counties in full. */
export function switcherName(worked: County[]): string {
  if (worked.length === 1) return worked[0]?.display ?? "";
  return `${workedLabel(worked)}: ${worked.map((county) => county.display).join(", ")}`;
}

/** The county a lead row names, as "Los Angeles" from "Los Angeles, CA". */
export function countyName(county: string): string {
  return county.split(",")[0]?.trim() ?? county;
}

/**
 * The worked counties as one string. A page that keeps its previous rows while new ones
 * load is keyed by it, so a county switch starts the page afresh and never shows the
 * previous counties' rows under the new header (Cook's letters under Harris, 7 October).
 */
export function useWorkedKey(): string {
  const { worked } = useWorkedCounties();
  return worked.map((county) => county.key).join("|");
}

/** Turns one county on or off; the last county on cannot be turned off. */
export function useToggleCounty() {
  const { worked } = useWorkedCounties();
  const save = useSaveCounties();
  const toggle = (county: County, on: boolean) => {
    const keys = worked.map((c) => c.key).filter((key) => key !== county.key);
    if (on) keys.push(county.key);
    if (keys.length) save.mutate(keys);
  };
  const isLast = (county: County) => county.selected && worked.length === 1;
  return { toggle, isLast, saving: save.isPending };
}

/** "A", "A and B", "A, B and C". */
export function listWords(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** One state's live counties, as the county choosers list them. */
export type StateCounties = { code: string; name: string; counties: County[]; leads: number };

/** "Texas" from "Harris County, Texas"; the state code when the display names no state. */
export function stateName(county: County): string {
  const parts = county.display.split(",");
  return parts.length > 1 ? (parts[parts.length - 1]?.trim() ?? county.state) : county.state;
}

/** The counties grouped by state, states and counties in alphabetical order. */
export function byState(counties: County[]): StateCounties[] {
  const states = new Map<string, StateCounties>();
  for (const county of counties) {
    const state = states.get(county.state) ?? { code: county.state, name: stateName(county), counties: [], leads: 0 };
    state.counties.push(county);
    state.leads += county.leads;
    states.set(county.state, state);
  }
  const alphabetical = (a: string, b: string) => a.localeCompare(b, "en-US");
  return [...states.values()]
    .map((state) => ({ ...state, counties: [...state.counties].sort((a, b) => alphabetical(a.name, b.name)) }))
    .sort((a, b) => alphabetical(a.name, b.name));
}

/**
 * The states whose name or code, or any of whose counties, match the search; an empty search
 * keeps all. A state kept for some of its counties counts only their leads.
 */
export function matchStates(states: StateCounties[], search: string): StateCounties[] {
  const q = search.trim().toLowerCase();
  if (!q) return states;
  return states.flatMap((state) => {
    if (state.name.toLowerCase().includes(q) || state.code.toLowerCase() === q) return [state];
    const counties = state.counties.filter((county) => county.name.toLowerCase().includes(q));
    return counties.length ? [{ ...state, counties, leads: counties.reduce((sum, county) => sum + county.leads, 0) }] : [];
  });
}
